import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export interface StockDrawerContextValue {
  isOpen: boolean;
  symbol: string | null;
  openStockDrawer: (symbol: string) => void;
  closeStockDrawer: () => void;
}

const StockDrawerContext = createContext<StockDrawerContextValue | undefined>(undefined);

export interface StockDrawerProviderProps {
  children: React.ReactNode;
}

export const StockDrawerProvider: React.FC<StockDrawerProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [symbol, setSymbol] = useState<string | null>(null);

  const openStockDrawer = useCallback((targetSymbol: string) => {
    if (!targetSymbol) return;
    setSymbol(targetSymbol.trim().toUpperCase());
    setIsOpen(true);
  }, []);

  const closeStockDrawer = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      symbol,
      openStockDrawer,
      closeStockDrawer,
    }),
    [isOpen, symbol, openStockDrawer, closeStockDrawer]
  );

  return <StockDrawerContext.Provider value={value}>{children}</StockDrawerContext.Provider>;
};

export const useStockDrawer = (): StockDrawerContextValue => {
  const context = useContext(StockDrawerContext);
  if (!context) {
    throw new Error('useStockDrawer must be used within a StockDrawerProvider');
  }
  return context;
};

export const useOptionalStockDrawer = (): StockDrawerContextValue | null => {
  return useContext(StockDrawerContext) ?? null;
};

export default StockDrawerContext;

